import React, { useState } from 'react';
import { BookImage } from '../types';
import { getImageUrl } from '../services/api';

interface ImageReorderProps {
  images: BookImage[];
  onReorder: (reorderedImages: BookImage[]) => void;
  onRemove: (imageId: number) => void;
}

export function ImageReorder({ images, onReorder, onRemove }: ImageReorderProps): React.ReactElement {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const reordered = [...images];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, draggedItem);

    onReorder(reordered);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div style={styles.container}>
      <div style={styles.hint}>
        💡 Arraste as imagens para reordenar
      </div>
      <div style={styles.grid}>
        {images.map((img, index) => (
          <div
            key={img.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              ...styles.imageContainer,
              opacity: draggedIndex === index ? 0.5 : 1,
              transform: dragOverIndex === index && draggedIndex !== index ? 'scale(1.05)' : 'scale(1)',
              border: dragOverIndex === index && draggedIndex !== index ? '2px dashed var(--color-primary)' : '2px solid var(--color-border-default)',
            }}
          >
            <div style={styles.orderBadge}>{index + 1}</div>
            <img 
              src={getImageUrl(img.filepath)} 
              alt={`Imagem ${index + 1}`} 
              style={styles.image}
            />
            <button
              type="button"
              onClick={() => onRemove(img.id)}
              style={styles.removeBtn}
              title="Remover imagem"
            >
              ×
            </button>
            <div style={styles.dragHandle}>⋮⋮</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '10px',
  },
  hint: {
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    marginBottom: '12px',
    padding: '8px 12px',
    background: 'var(--color-bg-secondary)',
    borderRadius: '8px',
    borderLeft: '3px solid var(--color-primary)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '12px',
  },
  imageContainer: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'move',
    transition: 'all 0.2s ease',
    background: '#fff',
  },
  orderBadge: {
    position: 'absolute',
    top: '8px',
    left: '8px',
    background: 'rgba(var(--color-primary-rgb), 0.9)',
    color: 'white',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 'bold',
    zIndex: 2,
  },
  image: {
    width: '100%',
    height: '120px',
    objectFit: 'cover',
    display: 'block',
  },
  removeBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(220, 38, 38, 0.9)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  dragHandle: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    color: 'var(--color-text-secondary)',
    fontSize: '16px',
    fontWeight: 'bold',
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '2px 6px',
    borderRadius: '4px',
    letterSpacing: '-2px',
  },
};
